import { Action } from 'redux';

import {
    ContactPattern,
    ValidationStatus,
    ContactsOverview,
    Contact,
    ContactHistory,
    ContactsGrowth,
    ContactStatus,
    ConsentTracking,
    ContactsOrderBy,
    ContactFieldType,
    UnsubscribedOverview,
    UnsubscribedContactsOrderBy,
    ContactInfo,
    ContactUnsubscribeReason,
} from '@dev/ee-api/ee-api-models';
import { ContactsUnsubscribedParams, ExportContactsUnsubscribedParams } from '@api/services/ElasticApi';
import { AxiosError } from 'axios';
import { ShowDialogParams } from '../dialog/types';

export interface ContactsInterface {
    contactPattern?: ContactPattern;
    validationStatus?: ValidationInterface;
    encodingName?: string;
    postContactsParams?: PostContactsParams;
    collection?: Contact[];
    counts?: ContactCounts;
    isCreatingContacts?: boolean;
    isContactsLoading?: boolean;
    selectedContact?: Contact;
    contactsOverview?: ContactsOverview;
    contactHistory?: ContactHistory[];
    isContactHistoryLoading: boolean;
    contactHistoryCounts?: number;
    contactsGrowth?: ContactsGrowth[];
    isContactsGrowthLoading?: boolean;
    isShowingUploadConfirmation: boolean;
    unsubscribedContacts: UnsubscribedContactsInterface;
}

export interface UnsubscribedContactsInterface {
    overview: UnsubscribedOverview;
    loadingUnsubscribedOverview: boolean;
    contactsList: ContactInfo[];
    pagination: ContactsUnsubscribedPaginationInterface;
    loadingUnsubscribed: boolean;
    statusCount: UnsubscribedContactsStatusCounts;
    pickedContactEmail: string;
    unsubReasons: Array<ContactUnsubscribeReason>;
}

export interface ContactCounts {
    totalCount: number;
    activeCount: number;
    engagedCount: number;
    bouncedCount: number;
    unsubscribedCount: number;
    abuseCount: number;
    inactiveCount: number;
    staleCount: number;
    transactionalCount: number;
}

export interface ContactFieldsInterface {
    email: string;
    firstName?: string;
    lastName?: string;
    customFields?: any;
}

export interface PostWithPatternParams {
    listName: string;
    createList?: boolean;
    status?: ContactStatus;
    encodingName?: string;
    consentTracking?: ConsentTracking;
    FileName: string;
    Pattern: Record<string, string>;
}

export interface PostContactsParams {
    listName: string;
    createList?: boolean;
    status?: ContactStatus;
    encodingName?: string;
    consentTracking?: ConsentTracking;
    createContactValidationLog?: boolean;
    body: string[] | any[];
}

export interface PutFieldParams {
    name: string;
    type: ContactFieldType;
    length?: number;
}

export interface ContactsBulkUpdate {
    emails: string[];
    field: string;
    value: string;
}

export interface SingleContactPaginationInterface {
    email: string;
    limit?: number;
    offset?: number;
}

export interface GetContactsInterface {
    rule?: string;
    limit?: number;
    offset?: number;
    search?: string;
    statuses?: ContactStatus[];
    orderBy?: ContactsOrderBy;
}

export enum ContactsActionTypes {
    CREATE_SINGLE = '@@contacts/CREATE_SINGLE',
    CREATE_MULTIPLE = '@@contacts/CREATE_MULTIPLE',
    CREATE_START = '@@contacts/CREATE_START',
    CREATE_ERROR = '@@contacts/CREATE_ERROR',
    CREATE_SUCCESS = '@@contacts/CREATE_SUCCESS',
    FETCH_PATTERN_START = '@@contacts/FETCH_PATTERN_START',
    FETCH_PATTERN_ERROR = '@@contacts/FETCH_PATTERN_ERROR',
    FETCH_PATTERN_SUCCESS = '@@contacts/FETCH_PATTERN_SUCCESS',
    CREATE_WITH_PATTERN_SUCCESS = '@@contacts/CREATE_WITH_PATTERN_SUCCESS',
    CREATE_WITH_PATTERN_ERROR = '@@contacts/CREATE_WITH_PATTERN_ERROR',
    CREATE_WITH_PATTERN_START = '@@contacts/CREATE_WITH_PATTERN_START',
    SET_POST_PARAMS = '@@contacts/SET_POST_PARAMS',
    SET_POST_START = '@@contacts/SET_POST_START',
    FETCH_OVERVIEW_START = '@@contacts/FETCH_OVERVIEW_START',
    FETCH_OVERVIEW_ERROR = '@@contacts/FETCH_OVERVIEW_ERROR',
    FETCH_OVERVIEW_SUCCESS = '@@contacts/FETCH_OVERVIEW_SUCCESS',
    FETCH_START = '@@contacts/FETCH_START',
    FETCH_SUCCESS = '@@contacts/FETCH_SUCCESS',
    FETCH_ERROR = '@@contacts/FETCH_ERROR',
    FETCH_CONTACT_START = '@@contacts/FETCH_CONTACT_START',
    FETCH_CONTACT_SUCCESS = '@@contacts/FETCH_CONTACT_SUCCESS',
    FETCH_CONTACT_ERROR = '@@contacts/FETCH_CONTACT_ERROR',
    DESELECT = '@@contacts/DESELECT',
    DELETE_START = '@@contacts/DELETE_START',
    DELETE_SUCCESS = '@@contacts/DELETE_SUCCESS',
    DELETE_ERROR = '@@contacts/DELETE_ERROR',
    DELETE_BULK_UPDATE_START = '@@contacts/DELETE_BULK_UPDATE_START',
    DELETE_BULK_UPDATE_SUCCESS = '@@contacts/DELETE_BULK_UPDATE_SUCCESS',
    DELETE_BULK_UPDATE_ERROR = '@@contacts/DELETE_BULK_UPDATE_ERROR',
    BULK_UPDATE_START = '@@contacts/BULK_UPDATE_START',
    BULK_UPDATE_SUCCESS = '@@contacts/BULK_UPDATE_SUCCESS',
    BULK_UPDATE_ERROR = '@@contacts/BULK_UPDATE_ERROR',
    UPDATE_START = '@@contacts/UPDATE_START',
    UPDATE_SUCCESS = '@@contacts/UPDATE_SUCCESS',
    UPDATE_ERROR = '@@contacts/UPDATE_ERROR',
    GET_HISTORY_START = '@@contacts/GET_HISTORY_START',
    GET_HISTORY_SUCCESS = '@@contacts/GET_HISTORY_SUCCESS',
    GET_HISTORY_ERROR = '@@contacts/GET_HISTORY_ERROR',
    GET_GROWTH_START = '@@contacts/GET_GROWTH_START',
    GET_GROWTH_SUCCESS = '@@contacts/GET_GROWTH_SUCCESS',
    GET_GROWTH_ERROR = '@@contacts/GET_GROWTH_ERROR',
    CLEAR_UPLOAD_DATA = '@@contacts/CLEAR_UPLOAD_DATA',
    CLEAR_CONTACTS_AND_COUNTS = '@@contacts/CLEAR_CONTACTS_AND_COUNTS',
    FETCH_NEW_SUCCESS = '@@contacts/FETCH_NEW_SUCCESS',
    GET_CONTACTS_UNSUBSCRIBED_OVERVIEW = '@@contacts/GET_CONTACTS_UNSUBSCRIBED_OVERVIEW',
    GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_SUCCESS = '@@contacts/GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_SUCCESS',
    GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_ERROR = '@@contacts/GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_ERROR',
    GET_CONTACTS_UNSUBSCRIBED = '@@contacts/GET_CONTACTS_UNSUBSCRIBED',
    GET_CONTACTS_UNSUBSCRIBED_SUCCESS = '@@contacts/GET_CONTACTS_UNSUBSCRIBED_SUCCESS',
    GET_CONTACTS_UNSUBSCRIBED_ERROR = '@@contacts/GET_CONTACTS_UNSUBSCRIBED_ERROR',
    EXPORT_CONTACTS_UNSUBSCRIBED = '@@contacts/EXPORT_CONTACTS_UNSUBSCRIBED',
    SET_PICKED_CONTACT_EMAIL = '@@contacts/SET_PICKED_CONTACT_EMAIL',
}

export interface ContactsUnsubscribedPaginationInterface {
    from?: string;
    to?: string;
    statuses?: ContactStatus[];
    orderBy?: UnsubscribedContactsOrderBy;
    email?: string;
    activePage: number;
    perPage: number;
}

export interface AddSingleContactAction extends Action {
    type: ContactsActionTypes.CREATE_SINGLE;
    payload: PostContactsParams;
    redirectPath?: string;
}

export interface AddMultipleContactsAction extends Action {
    type: ContactsActionTypes.CREATE_MULTIPLE;
    payload: PostContactsParams;
    meta: { thunk: boolean };
}
export interface PostContactsFetchStartAction extends Action {
    type: ContactsActionTypes.CREATE_START;
    payload: PostContactsParams;
    meta?: { thunk: boolean };
}

export interface PostContactsFetchErrorAction extends Action {
    type: ContactsActionTypes.CREATE_ERROR;
    meta: Record<string, unknown>;
    error: boolean;
}

export interface ValidationInterface {
    validationStatus: ValidationStatus;
    Contacts: number;
}

export interface PostContactsFetchSuccessAction extends Action {
    type: ContactsActionTypes.CREATE_SUCCESS;
    payload: {
        validationStatus: ValidationInterface;
    };
    meta: Record<string, unknown>;
}

export interface GetContactsPatternFetchStartAction extends Action {
    type: ContactsActionTypes.FETCH_PATTERN_START;
    payload: {
        fileName: string;
        encodingName: string;
        errorModal?: ShowDialogParams;
    };
}

export interface GetContactsPatternFetchErrorAction extends Action {
    type: ContactsActionTypes.FETCH_PATTERN_ERROR;
}

export interface GetContactsPatternFetchSuccessAction extends Action {
    type: ContactsActionTypes.FETCH_PATTERN_SUCCESS;
    payload: {
        contactPattern: ContactPattern;
    };
}

export interface PostContactsWithPatternFetchStartAction extends Action {
    type: ContactsActionTypes.CREATE_WITH_PATTERN_START;
    payload: PostWithPatternParams;
    meta: { thunk: boolean };
}

export interface PostContactsWithPatternFetchErrorAction extends Action {
    type: ContactsActionTypes.CREATE_WITH_PATTERN_ERROR;
    error: boolean;
    meta: Record<string, unknown>;
}

export interface PostContactsWithPatternFetchSuccessAction extends Action {
    type: ContactsActionTypes.CREATE_WITH_PATTERN_SUCCESS;
    payload: {
        contacts: number;
        validationStatus: ValidationStatus;
        isShowingUploadConfirmation: boolean;
    };
    meta: Record<string, unknown>;
}

export interface SetAddContactParams extends Action {
    type: ContactsActionTypes.SET_POST_PARAMS | ContactsActionTypes.SET_POST_START;
    payload: {
        postContactsParams: PostContactsParams;
    };
}

export interface ContactsOverviewFetchStartAction extends Action {
    type: ContactsActionTypes.FETCH_OVERVIEW_START;
    payload: {
        from: string;
        to: string;
    };
}

export interface ContactsOverviewFetchErrorAction extends Action {
    type: ContactsActionTypes.FETCH_OVERVIEW_ERROR;
}

export interface ContactsOverviewFetchSuccessAction extends Action {
    type: ContactsActionTypes.FETCH_OVERVIEW_SUCCESS;
    payload: {
        contactsOverview;
    };
}

export interface ContactsFetchStartAction extends Action {
    type: ContactsActionTypes.FETCH_START;
    payload: {
        params: GetContactsInterface;
        isContactsLoading: boolean;
    };
}

export interface ContactsFetchErrorAction extends Action {
    type: ContactsActionTypes.FETCH_ERROR;
    payload: {
        isContactsLoading: boolean;
        collection: Contact[];
    };
}

export interface ContactsFetchSuccessAction extends Action {
    type: ContactsActionTypes.FETCH_SUCCESS;
    payload: {
        collection: Contact[];
        counts: ContactCounts;
        isContactsLoading: boolean;
    };
}

export interface SingleContactFetchStartAction extends Action {
    type: ContactsActionTypes.FETCH_CONTACT_START;
    payload: {
        email: string;
        isContactsLoading: boolean;
        showErrorToast: boolean;
    };
    meta: Record<string, unknown>;
}

export interface SingleContactFetchErrorAction extends Action {
    type: ContactsActionTypes.FETCH_CONTACT_ERROR;
    payload: {
        error: AxiosError | Error;
        isContactsLoading: boolean;
    };
    meta: Record<string, unknown>;
    error: boolean;
}

export interface SingleContactFetchSuccessAction extends Action {
    type: ContactsActionTypes.FETCH_CONTACT_SUCCESS;
    payload: {
        selectedContact: Contact;
        isContactsLoading: boolean;
    };
    meta: Record<string, unknown>;
}

export interface DeselectContactAction extends Action {
    type: ContactsActionTypes.DESELECT;
}

export interface ContactsDeleteParams {
    rule?: string;
    emails?: string[];
}

export interface ContactsLoadParams {
    rule?: string;
    statuses?: any;
    limit?: number;
    offset?: number;
    search?: string;
    orderBy?: ContactsOrderBy;
}

export interface ContactsDeleteStartAction extends Action {
    type: ContactsActionTypes.DELETE_START;
    payload: {
        params: {
            deleteParams: ContactsDeleteParams;
            loadParams: ContactsLoadParams;
        };
    };
}

export interface ContactsDeleteErrorAction extends Action {
    type: ContactsActionTypes.DELETE_ERROR;
}

export interface ContactsDeleteSuccessAction extends Action {
    type: ContactsActionTypes.DELETE_SUCCESS;
    payload: {
        collection: Contact[];
    };
}

export interface ContactsBulkUpdateStartAction extends Action {
    type: ContactsActionTypes.BULK_UPDATE_START;
    payload: {
        params: ContactsBulkUpdate;
    };
    meta: {
        thunk: boolean;
    };
}

export interface ContactsBulkUpdateErrorAction extends Action {
    type: ContactsActionTypes.BULK_UPDATE_ERROR;
    error: boolean;
    meta: Record<string, any>;
}

export interface ContactsBulkUpdateSuccessAction extends Action {
    type: ContactsActionTypes.BULK_UPDATE_SUCCESS;
    meta: Record<string, any>;
}

export interface ContactUpdateStartAction extends Action {
    type: ContactsActionTypes.UPDATE_START;
    payload: {
        params: ContactFieldsInterface;
        isContactUpdating: boolean;
    };
}

export interface ContactUpdateErrorAction extends Action {
    type: ContactsActionTypes.UPDATE_ERROR;
    payload: {
        isContactUpdating: boolean;
    };
}

export interface ContactUpdateSuccessAction extends Action {
    type: ContactsActionTypes.UPDATE_SUCCESS;
    payload: {
        selectedContact: Contact;
        isContactUpdating: boolean;
    };
}

export interface ContactGetHistoryStartAction extends Action {
    type: ContactsActionTypes.GET_HISTORY_START;
    payload: {
        params: SingleContactPaginationInterface;
        isContactHistoryLoading: boolean;
    };
}

export interface ContactGetHistoryErrorAction extends Action {
    type: ContactsActionTypes.GET_HISTORY_ERROR;
    payload: {
        isContactHistoryLoading: boolean;
    };
}

export interface ContactGetHistorySuccessAction extends Action {
    type: ContactsActionTypes.GET_HISTORY_SUCCESS;
    payload: {
        contactHistory: ContactHistory[];
        contactHistoryCounts: number;
        isContactHistoryLoading: boolean;
    };
}

export interface ContactGrowth {
    Day: string;
    ActiveCount: number;
    EngagedCount: number;
}

export interface ContactsGetGrowthStartAction extends Action {
    type: ContactsActionTypes.GET_GROWTH_START;
    payload: {
        from: string;
        to: string;
        isContactsGrowthLoading: boolean;
    };
}

export interface ContactsGetGrowthErrorAction extends Action {
    type: ContactsActionTypes.GET_GROWTH_ERROR;
    payload: {
        isContactsGrowthLoading: boolean;
    };
}

export interface ContactsGetGrowthSuccessAction extends Action {
    type: ContactsActionTypes.GET_GROWTH_SUCCESS;
    payload: {
        contactsGrowth: ContactGrowth[];
        isContactsGrowthLoading: boolean;
    };
}

export interface ClearUploadDataAction extends Action {
    type: ContactsActionTypes.CLEAR_UPLOAD_DATA;
}

export interface ClearContactsAndCountsAction extends Action {
    type: ContactsActionTypes.CLEAR_CONTACTS_AND_COUNTS;
}

export interface GetContactsUnsubscribedOverviewAction extends Action {
    type: ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW;
    payload: {
        from: string;
        to: string;
    };
}

export interface GetContactsUnsubscribedOverviewSuccessAction extends Action {
    type: ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_SUCCESS;
    payload: UnsubscribedContactsInterface;
}

export interface GetContactsUnsubscribedOverviewErrorAction extends Action {
    type: ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_ERROR;
}

export interface GetContactsUnsubscribedAction extends Action {
    type: ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED;
    payload: ContactsUnsubscribedParams;
}

export interface GetContactsUnsubscribedSuccessAction extends Action {
    type: ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_SUCCESS;
    payload: UnsubscribedContactsInterface;
}

export interface GetContactsUnsubscribedErrorAction extends Action {
    type: ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_ERROR;
}

export interface UnsubscribedContactsStatusCounts {
    total: number;
    unsubscribed: number;
    abused: number;
}

export interface ExportContactsUnsubscribedAction extends Action {
    type: ContactsActionTypes.EXPORT_CONTACTS_UNSUBSCRIBED;
    payload: ExportContactsUnsubscribedParams;
}

export interface SetPickedContactEmailAction extends Action {
    type: ContactsActionTypes.SET_PICKED_CONTACT_EMAIL;
    payload: {
        pickedContactEmail: string;
    };
}
